import classNames from 'classnames';
import React, { forwardRef, useId } from 'react';
import FormControl from '../../../FormControl';
import './ToggleButton.scss';

export interface ToggleButtonProps {
  label: React.ReactNode | React.ReactNode[];
  name: string;
  disabled?: boolean;
  required?: boolean;
  error?: string | boolean;
  defaultChecked?: boolean;
  checked?: boolean;
  value?: string | number | boolean;
  tooltip?: string | React.ReactNode | React.ReactNode[];
  tooltipAriaLabel?: string;
  hideLabel?: boolean;
  pii?: boolean;
  inputProps?: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const ToggleButton = forwardRef<HTMLInputElement, ToggleButtonProps>(
  (
    {
      label,
      name,
      disabled = false,
      required = false,
      pii = false,
      value,
      error,
      checked,
      defaultChecked,
      tooltip,
      tooltipAriaLabel,
      inputProps = {},
      onChange,
      onBlur,
      onFocus
    },
    ref
  ) => {
    const id = useId();

    const classes = classNames('mss-toggle-button zb-button', {
      'zb-button-primary': checked,
      'zb-button-secondary': !checked
    });

    return (
      <FormControl
        id={id}
        tooltip={tooltip}
        tooltipAriaLabel={tooltipAriaLabel}
        error={error}
        required={required}
        pii={pii}
      >
        <label htmlFor={id} className={classes}>
          {label}

          <input
            type="radio"
            id={id}
            name={name}
            disabled={disabled}
            aria-invalid={!!error}
            aria-describedby={error ? `error-${id}` : undefined}
            aria-required={required}
            aria-checked={checked}
            // @ts-expect-error boolean is not an allowed value
            value={value}
            checked={checked}
            defaultChecked={defaultChecked}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            ref={ref}
            className="sr-only"
            {...inputProps}
          />
        </label>
      </FormControl>
    );
  }
);

export default ToggleButton;
